import { ref, onMounted, onUnmounted } from 'vue';

export interface DraggableWindowOptions {
    initialWidth?: number;
    initialHeight?: number;
    initialX?: number;
    initialY?: number;
    minWidth?: number;
    minHeight?: number;
    snapThreshold?: number;
    topOffset?: number;
    windowId?: string;
    /** 跟随外壳卡片的内容盒（上下边对齐）。见下方 shellPinned 的说明。 */
    followShell?: boolean;
}

import { useOmniStore } from '../stores/omni';

export function useDraggableWindow(options: DraggableWindowOptions = {}) {
    const {
        initialWidth = 900,
        initialHeight = 600,
        minWidth = 400,
        minHeight = 300,
        snapThreshold = 20,
        topOffset = 0,
        followShell = false
    } = options;

    const isDragging = ref(false);
    const isResizing = ref(false);
    const winPos = ref({ x: 0, y: 0 });
    const winSize = ref({ w: initialWidth, h: initialHeight });

    const store = useOmniStore();
    let ro: ResizeObserver | null = null;

    // ── 跟随外壳卡片（S1 的补丁）───────────────────────────────────────────
    // 为什么需要它：窗口矩形是**持久化**的（omni-active-windows）。只把初始位置算对，
    // 第二次加载会读到旧矩形，浮窗顶又比卡片高十几像素 —— 三栏头部线再次错开，
    // 而这一次没有任何代码在错。所以"对齐"必须是一个**持续行为**：
    //   · 未拖未缩 → 每次挂载与每次窗口尺寸变化都重新对齐；
    //   · 用户拖过/缩放过 → 本次会话交出控制权（尊重人的操作）；
    //   · 下次加载 → 回到对齐态（对齐是布局契约，不是用户的偏好）。
    const shellPinned = ref(followShell);

    function alignToShell() {
        const shell = document.querySelector('.workspace-frost-shell');
        const inner = document.querySelector('[data-shell-content]');
        if (!shell || !inner) return false;
        const box = shell.getBoundingClientRect();
        const ics = getComputedStyle(inner);
        const ir = inner.getBoundingClientRect();
        // 卡片内底是硬上限：内容溢出时 p-4 会变高，而浮窗不能超出可视卡片。
        const limitBottom = box.bottom - (parseFloat(getComputedStyle(shell).borderBottomWidth) || 0);
        const top = ir.top + (parseFloat(ics.paddingTop) || 0);
        const bottom = Math.min(ir.bottom - (parseFloat(ics.paddingBottom) || 0), limitBottom);
        winSize.value.h = Math.max(200, bottom - top);
        winPos.value.x = Math.max(0, window.innerWidth - winSize.value.w);
        winPos.value.y = Math.max(0, top);
        return true;
    }

    function syncStore() {
        if (options.windowId) {
            store.setWindowRect(options.windowId, {
                x: winPos.value.x,
                y: winPos.value.y,
                w: winSize.value.w,
                h: winSize.value.h
            });
        }
    }

    function applySnapping(x: number, y: number, w: number, h: number) {
        let nx = x;
        let ny = y;
        let nw = w;
        let nh = h;
        const maxW = window.innerWidth;
        const maxH = window.innerHeight;

        // Soft boundaries for global resize: Ensure we don't exceed screen dimensions abruptly
        if (nw > maxW) nw = maxW;
        if (nh > maxH) nh = maxH;

        // Only snap if we are very close to the edge, but allow breaking out
        if (nx < snapThreshold && nx > -snapThreshold) nx = 0;
        if (nx + nw > maxW - snapThreshold && nx + nw < maxW + snapThreshold) nx = maxW - nw;

        if (ny < snapThreshold + topOffset && ny > topOffset - snapThreshold) ny = topOffset;
        if (ny + nh > maxH - snapThreshold && ny + nh < maxH + snapThreshold) ny = maxH - nh;

        // Ensure soft visibility boundaries are respected even on window resize
        const minVisibleW = 100;
        const minVisibleH = 40;
        if (nx < -nw + minVisibleW) nx = -nw + minVisibleW;
        if (ny < topOffset) ny = topOffset; // Header must always be visible
        if (nx > maxW - minVisibleW) nx = maxW - minVisibleW;
        if (ny > maxH - minVisibleH) ny = maxH - minVisibleH;

        return { nx, ny, nw, nh };
    }

    function handleWindowResize() {
        if (shellPinned.value && alignToShell()) return;   // 跟随态：布局变了就回到卡片
        const snapped = applySnapping(winPos.value.x, winPos.value.y, winSize.value.w, winSize.value.h);
        winPos.value.x = snapped.nx;
        winPos.value.y = snapped.ny;
        winSize.value.w = snapped.nw;
        winSize.value.h = snapped.nh;
        syncStore();
    }

    onMounted(() => {
        const existingRect = options.windowId ? store.activeWindows[options.windowId] : null;

        if (shellPinned.value && existingRect) {
            // 跟随态：只沿用宽度（宽度是用户的），上下边一律回到卡片。
            winSize.value.w = existingRect.w || initialWidth;
            alignToShell();
        } else if (existingRect) {
            winSize.value.w = existingRect.w;
            winSize.value.h = existingRect.h;
            winPos.value.x = existingRect.x;
            winPos.value.y = existingRect.y;
        } else {
            // Initial default position with bounds checking
            winSize.value.w = Math.min(initialWidth, window.innerWidth * 0.85);
            winSize.value.h = Math.min(initialHeight, window.innerHeight * 0.85);
            
            // Use custom initial pos if specified, otherwise center
            if (options.initialX !== undefined && options.initialY !== undefined) {
                winPos.value.x = options.initialX;
                winPos.value.y = options.initialY;
            } else if (options.windowId === 'social-sidebar') {
                // 右栏是浮窗，但上下边要对齐卡片的内容盒 —— 否则三栏头部线不在同一条基线上。
                if (!(shellPinned.value && alignToShell())) {
                    winPos.value.x = Math.max(0, window.innerWidth - winSize.value.w);
                    winPos.value.y = Math.max(0, (window.innerHeight - winSize.value.h) / 2);
                }
            } else {
                winPos.value.x = Math.max(0, (window.innerWidth - winSize.value.w) / 2);
                winPos.value.y = Math.max(0, (window.innerHeight - winSize.value.h) / 2);
            }
        }
        
        handleWindowResize(); // Apply snapping and boundaries to the initialized values
        window.addEventListener('resize', handleWindowResize);
        // 卡片自己的尺寸会变（路由切换、输入岛收起、状态条展开）—— 窗口 resize 事件盖不住这些，
        // 实测因此量到过 494px 的落差（浮窗停在初次挂载时的高度）。跟随态下盯住内容盒。
        if (shellPinned.value) {
            // 晚挂载重试：右栏可能先于路由视图挂载，那一刻卡片还是空的（实测会量到 200px 的地板值），
            // 之后内容出现、卡片变高 —— 没有观察器就再也没有人对齐了。
            const attach = (tries = 0) => {
                const anchor = document.querySelector('[data-shell-content]') ?? document.querySelector('.workspace-frost-shell');
                if (anchor) {
                    ro = new ResizeObserver(() => { if (shellPinned.value) alignToShell(); });
                    ro.observe(anchor);
                    alignToShell();
                } else if (tries < 25) {
                    setTimeout(() => attach(tries + 1), 100);
                }
            };
            attach();
        }
    });

    onUnmounted(() => {
        window.removeEventListener('resize', handleWindowResize);
        ro?.disconnect(); ro = null;
        // Do not remove window rect on unmount to persist across toggles!
    });

    function startDrag(e: PointerEvent) {
        if ((e.target as HTMLElement).closest('button, input, .no-drag')) return;
        shellPinned.value = false;
        isDragging.value = true;
        const startX = e.clientX;
        const startY = e.clientY;
        const startPosX = winPos.value.x;
        const startPosY = winPos.value.y;
        let animationFrameId: number | null = null;

        function onPointerMove(ev: PointerEvent) {
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId);
            }
            animationFrameId = requestAnimationFrame(() => {
                let nx = startPosX + (ev.clientX - startX);
                let ny = startPosY + (ev.clientY - startY);
                
                // Dynamic Drag Snapping
                const maxW = window.innerWidth;
                const maxH = window.innerHeight;
                
                if (nx < snapThreshold && nx > -snapThreshold) nx = 0;
                if (ny < snapThreshold + topOffset && ny > topOffset - snapThreshold) ny = topOffset;
                if (nx + winSize.value.w > maxW - snapThreshold && nx + winSize.value.w < maxW + snapThreshold) nx = maxW - winSize.value.w;
                if (ny + winSize.value.h > maxH - snapThreshold && ny + winSize.value.h < maxH + snapThreshold) ny = maxH - winSize.value.h;

                // Soft boundaries: Ensure at least a part of the window remains grabbable
                const minVisibleW = 100;
                const minVisibleH = 40;
                
                if (nx < -winSize.value.w + minVisibleW) nx = -winSize.value.w + minVisibleW;
                if (ny < topOffset) ny = topOffset; // Header must always be visible
                if (nx > maxW - minVisibleW) nx = maxW - minVisibleW;
                if (ny > maxH - minVisibleH) ny = maxH - minVisibleH;

                winPos.value.x = nx;
                winPos.value.y = ny;
                syncStore();
                animationFrameId = null;
            });
        }

        function onPointerUp() {
            isDragging.value = false;
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
            }
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', onPointerUp);
        }

        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
    }

    function startResize(e: PointerEvent, dir: string = 'se') {
        // 只让**竖向**的缩放交出跟随权：宽度是用户的（改宽不该把上下边也交出去）
        if (/(top|bottom|^[ns]$|[ns]-|[ns]$)/.test(dir)) shellPinned.value = false;
        e.stopPropagation();
        isResizing.value = true;
        const startX = e.clientX;
        const startY = e.clientY;
        const startW = winSize.value.w;
        const startH = winSize.value.h;
        const startPosX = winPos.value.x;
        const startPosY = winPos.value.y;
        let animationFrameId: number | null = null;

        function onPointerMove(ev: PointerEvent) {
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId);
            }
            animationFrameId = requestAnimationFrame(() => {
                let dx = ev.clientX - startX;
                let dy = ev.clientY - startY;

                let nw = startW;
                let nh = startH;
                let nx = startPosX;
                let ny = startPosY;

                if (dir.includes('e')) {
                    nw = startW + dx;
                } else if (dir.includes('w')) {
                    nw = startW - dx;
                    nx = startPosX + dx;
                }

                if (dir.includes('s')) {
                    nh = startH + dy;
                } else if (dir.includes('n')) {
                    nh = startH - dy;
                    ny = startPosY + dy;
                }

                // Min size constraints
                if (nw < minWidth) {
                    if (dir.includes('w')) nx = startPosX + (startW - minWidth);
                    nw = minWidth;
                }
                if (nh < minHeight) {
                    if (dir.includes('n')) ny = startPosY + (startH - minHeight);
                    nh = minHeight;
                }

                // Screen Boundaries
                const maxW = window.innerWidth;
                const maxH = window.innerHeight;

                const rightEdge = nx + nw;
                const bottomEdge = ny + nh;

                // Smart Edge Snapping - Only snap the axis being resized
                if (dir.includes('w')) {
                    if (nx < snapThreshold) {
                        nx = 0;
                        nw = rightEdge;
                    }
                } else if (dir.includes('e')) {
                    if (nx + nw > maxW - snapThreshold) {
                        nw = maxW - nx;
                    }
                }

                if (dir.includes('n')) {
                    if (ny < snapThreshold + topOffset) {
                        ny = topOffset;
                        nh = bottomEdge - topOffset;
                    }
                } else if (dir.includes('s')) {
                    if (ny + nh > maxH - snapThreshold) {
                        nh = maxH - ny;
                    }
                }

                // Final min size check after snap
                if (nw < minWidth) {
                    nw = minWidth;
                    if (dir.includes('w')) nx = rightEdge - nw;
                }
                if (nh < minHeight) {
                    nh = minHeight;
                    if (dir.includes('n')) ny = bottomEdge - nh;
                }

                winSize.value.w = nw;
                winSize.value.h = nh;
                winPos.value.x = nx;
                winPos.value.y = ny;
                syncStore();
                animationFrameId = null;
            });
        }

        function onPointerUp() {
            isResizing.value = false;
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
            }
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', onPointerUp);
        }

        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
    }

    return {
        isDragging,
        isResizing,
        winPos,
        winSize,
        startDrag,
        startResize
    };
}
